import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { KREDAFI_ICON_CACHE } from "../../utils/iconCacheBuster";
import { installPwaFromClick } from "../../utils/pwaInstall";
import "./PwaInstallPrompt.css";

interface PwaInstallPromptProps {
  theme?: "light" | "dark";
}

/**
 * Только iOS / Mac — пошаговая инструкция.
 * На Android Chrome сам показывает нативный баннер; кнопка на лендинге вызывает prompt().
 */
export const PwaInstallPrompt: React.FC<PwaInstallPromptProps> = ({
  theme = "light",
}) => {
  const themeClass = theme === "dark" ? "pwaDark" : "";
  const { t, i18n } = useTranslation();
  const { isInstalled, platform, canInstall } = usePwaInstall();
  const [modalOpen, setModalOpen] = useState(false);

  const isRu = (i18n.language || "en").startsWith("ru");
  const isAndroid = platform === "android";

  const closeModal = useCallback(() => setModalOpen(false), []);
  const openModal = useCallback(() => setModalOpen(true), []);

  const onBannerClick = useCallback(() => {
    if (isAndroid && canInstall) {
      installPwaFromClick();
      return;
    }
    openModal();
  }, [canInstall, isAndroid, openModal]);

  if (isInstalled) {
    return null;
  }

  const iconSrc = `/assets/kredafi-icon-192.png?v=${KREDAFI_ICON_CACHE}`;

  return (
    <>
      <div className={`pwaBanner ${themeClass}`} onClick={onBannerClick}>
        <div className="pwaBannerContent">
          <span className="pwaBannerIcon">📲</span>
          <span className="pwaBannerText">
            <div className="pwaBannerTitle">{t("Install KredaFi App")}</div>
            <div className="pwaBannerSubtitle">
              {platform === "mac"
                ? isRu ? "Добавьте в Dock на Mac" : "Add to Mac Dock"
                : platform === "ios"
                ? t("Quick access from your iPhone Home Screen")
                : t("Fast and secure access on your device")}
            </div>
          </span>
        </div>
        <div className="pwaBannerAction">
          {isAndroid && canInstall
            ? isRu ? "Установить" : t("Install")
            : platform === "ios" || platform === "mac"
            ? isRu ? "Инструкция" : "Guide"
            : isRu ? "Установить" : t("Install")}
        </div>
      </div>

      {modalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className={`pwaPromptOverlay ${themeClass}`} onClick={closeModal}>
            <div className="pwaPromptCard" onClick={(e) => e.stopPropagation()}>
              <div className="pwaPromptHeader">
                <div className="pwaPromptTitleRow">
                  <img
                    src={iconSrc}
                    alt="KredaFi"
                    className="pwaPromptAppIcon"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `/icon-192.png?v=${KREDAFI_ICON_CACHE}`;
                    }}
                  />
                  <div>
                    <h3 className="pwaPromptTitle">{t("Install KredaFi")}</h3>
                    <p className="pwaPromptSubtitle">kredafi.webtransfer.app</p>
                  </div>
                </div>
                <button type="button" className="pwaCloseButton" onClick={closeModal} aria-label="Close">
                  <CloseOutlined />
                </button>
              </div>

              {isAndroid ? (
                <>
                  <div className="pwaStepsList">
                    {canInstall ? (
                      <p className="pwaStepText">
                        {isRu
                          ? "Нажмите «Установить» — Chrome покажет системный диалог добавления KredaFi на главный экран."
                          : "Tap Install — Chrome will show the system dialog to add KredaFi to your home screen."}
                      </p>
                    ) : (
                      <>
                        <div className="pwaStepItem">
                          <div className="pwaStepIconBadge">⋮</div>
                          <div>
                            <div className="pwaStepNumber">{t("Step 1")}</div>
                            <p className="pwaStepText">
                              {isRu
                                ? "Меню Chrome (три точки) → «Установить приложение» или «Добавить на главный экран»"
                                : "Chrome menu (⋮) → Install app or Add to Home screen"}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="pwaPromptActions">
                    {canInstall ? (
                      <Button
                        type="primary"
                        size="large"
                        className="pwaPrimaryBtn"
                        onClick={() => {
                          installPwaFromClick();
                          closeModal();
                        }}
                      >
                        {isRu ? "Установить KredaFi" : t("Install KredaFi")}
                      </Button>
                    ) : (
                      <Button type="primary" size="large" className="pwaPrimaryBtn" onClick={closeModal}>
                        {isRu ? "Понятно" : "Got it"}
                      </Button>
                    )}
                  </div>
                </>
              ) : platform === "mac" ? (
                <>
                  <div className="pwaStepsList">
                    <div className="pwaStepItem">
                      <div className="pwaStepIconBadge">📂</div>
                      <div>
                        <div className="pwaStepNumber">{isRu ? "Шаг 1" : "Step 1"}</div>
                        <p className="pwaStepText">
                          Safari: <span className="pwaStepHighlight">File → Add to Dock</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="pwaPromptActions">
                    <Button type="primary" size="large" className="pwaPrimaryBtn" onClick={closeModal}>
                      {isRu ? "Понятно" : "Got it"}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="pwaStepsList">
                    <div className="pwaStepItem">
                      <div className="pwaStepIconBadge">📤</div>
                      <div>
                        <div className="pwaStepNumber">{t("Step 1")}</div>
                        <p className="pwaStepText">
                          {t("Tap the")} <span className="pwaStepHighlight">{t("Share button")}</span>
                        </p>
                      </div>
                    </div>
                    <div className="pwaStepItem">
                      <div className="pwaStepIconBadge">➕</div>
                      <div>
                        <div className="pwaStepNumber">{t("Step 2")}</div>
                        <p className="pwaStepText">
                          <span className="pwaStepHighlight">{t("Add to Home Screen")}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="pwaPromptActions">
                    <Button type="primary" size="large" className="pwaPrimaryBtn" onClick={closeModal}>
                      {t("Add to Home Screen")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PwaInstallPrompt;
